import { Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormGroup,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  ManagementService,
  SensorsSensor,
  SensorsService,
  TrigonosDatasource,
} from 'src/app/api/generated';
import { DataChangedService } from 'src/app/data-changed.service';
import { ReportModulesService } from 'src/app/services/report-modules/report-modules.service';
import {
  fieldsToOldRepresentation,
  GenericCorrelationConfig,
  getMinMaxFromConfig,
  MinMaxValue,
  sensor_to_field_map,
} from '../../correlation.tools';
import { SensorFieldCopyPasteService } from './sensor-field-copy-paste.service';
import { LanguageService } from 'src/app/services/language.service';

import * as uuid from 'uuid';
import { validTime } from 'src/app/tools/validators';
import { toNumberFromENString, toNumberStringDE } from 'src/app/user/tools';
import { moveItemInArray } from '@angular/cdk/drag-drop';

export interface VisualisableField {
  id: string;
  label: string;
  unit: string;
}

@Component({
    selector: 'app-correlation-generic-config',
    templateUrl: './correlation-config.component.html',
    styleUrls: ['./correlation-config.component.scss'],
    standalone: false
})
export class GenericCorrelationConfigComponent implements OnInit {
  public correlationForm: UntypedFormGroup;
  public availableDataSources: TrigonosDatasource[] = [];
  public availableSensors: Map<string, SensorsSensor[]> = undefined;
  public availableFields: VisualisableField[] = [];
  public editMode = false;
  public selectedFields = [];
  public defaultSelectedFieldControl = new UntypedFormControl();
  public availableSensorsForSecondYAxis: SensorsSensor[] = [];
  public availableFieldsForSecondYAxis: VisualisableField[] = [];
  public submitting = false;

  public fieldNameForId = (id: string): string => {
    const field = this.availableFields.find((f) => f.id === id);
    return field ? field.label : '';
  };

  public selectedStationsForField(formGroups: FormGroup[]): string {
    const enabled = formGroups.map((fg) => {
      return fg.controls.checked.value == true ? 1 : 0;
    });
    // Sum up the enabled stations
    const sum = enabled.reduce((a, b) => a + b, 0);
    return sum.toString();
  }

  public sensorControlsForField(fieldControl: AbstractControl): FormGroup[] {
    return (fieldControl.get('sensors') as FormArray<FormGroup>).controls;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: GenericCorrelationConfig,
    private dialogRef: MatDialogRef<GenericCorrelationConfig>,
    private formBuilder: UntypedFormBuilder,
    private reportModuleService: ReportModulesService,
    private dataChangedService: DataChangedService,
    private sensorService: SensorsService,
    private copyPasteService: SensorFieldCopyPasteService,
    private snackBar: MatSnackBar,
    private languageService: LanguageService
  ) {}

  public isSensorCheckedForFieldInConfig(
    sensor: string,
    field: string
  ): boolean {
    if (!this.editMode) {return false;}
    const sensors = this.data.sensorFields.filter(
      (sf) => sf.fieldName === field
    );

    if (sensors.length === 0) {
      return false;
    }

    const sensorIds = sensors[0].sensors.filter((s) => s.influx_name == sensor);

    if (sensorIds.length === 0) {
      return false;
    }

    return sensorIds[0].checked;
  }

  ngOnInit(): void {
    this.editMode = this.data !== null;
    this.correlationForm = this.formBuilder.group({
      guid: [this.editMode ? this.data?.guid : uuid.v4()],
      type: ['correlation-generic'],
      headline: [
        this.data?.headline ? this.data?.headline : '',
        Validators.required,
      ],
      labelXAxis: [
        this.data?.x_label ? this.data?.x_label : '',
        Validators.required,
      ],
      labelYAxis: [
        this.data?.y_label ? this.data?.y_label : '',
        Validators.required,
      ],
      // datasources: this.formBuilder.array([]),
      // visualisableFields: this.formBuilder.array([]),
      sensorFields: this.formBuilder.array([]),
      timePeriodFiltered:
        this.data?.filter.enabled === undefined
          ? false
          : this.data?.filter.enabled,
      timePeriodFilteredFrom: [
        this.data?.filter.hourStart ? this.data?.filter.hourStart : 22,
        [Validators.required, Validators.min(0), Validators.max(23)],
      ],
      timePeriodFilteredTill: [
        this.data?.filter.hourEnd ? this.data?.filter.hourEnd : 6,
        [Validators.required, Validators.min(0), Validators.max(23)],
      ],
      yAxisMinValues: this.formBuilder.array([]),
      relativeZero: [
        this.data?.relative_zero === undefined
          ? false
          : this.data.relative_zero,
      ],
      defaultDateRange: this.formBuilder.group({
        kind: [
          this.data?.defaultDateRange?.kind || 'zero',
          Validators.required,
        ],
        custom: this.formBuilder.group({
          start: [this.data?.defaultDateRange?.custom?.start || null],
          end: [this.data?.defaultDateRange?.custom?.end || null],
        }),
      }),
      defaultLowPassFilter: [this.data?.defaultLowPassFilter || '0'],
      defaultSelectedField: this.defaultSelectedFieldControl,
      secondYAxis: this.formBuilder.group({
        enabled: [this.data?.secondYAxis?.enabled || false],
        sensor: [this.data?.secondYAxis?.sensor || null],
        fieldName: [this.data?.secondYAxis?.fieldName || ''],
        label: [this.data?.secondYAxis?.label || ''],
        min_max: this.formBuilder.group({
          min: [this.data?.secondYAxis?.min_max?.min || 0],
          max: [this.data?.secondYAxis?.min_max?.max || 0],
        }),
      }),
    });

    this.sensorService
      .sensorsProjectRead(this.reportModuleService.currentReport.project)
      .subscribe((sensors) => {
        this.availableFields = fieldsToOldRepresentation(
          sensors,
          (value) => this.languageService.getTranslatedFieldName(value)
        );
        const fields_sensor_map = sensor_to_field_map(sensors);
        this.availableSensors = fields_sensor_map;

        // Sort the keys based on displayName
        const sortedFields = Array.from(fields_sensor_map.keys()).sort(
          (a, b) => {
            const displayNameA = this.fieldNameForId(a);
            const displayNameB = this.fieldNameForId(b);
            return displayNameA.localeCompare(displayNameB);
          }
        );

        for (const field of sortedFields) {
          const sensorsArray = this.formBuilder.array(
            fields_sensor_map
              .get(field)
              .sort((sensor1: SensorsSensor, sensor2: SensorsSensor) => {
                return sensor1.display_name.localeCompare(
                  sensor2.display_name,
                  undefined,
                  { numeric: true }
                );
              })
              .map((sensor) =>
                this.formBuilder.group({
                  checked: this.formBuilder.control(
                    this.isSensorCheckedForFieldInConfig(
                      sensor.influx_name,
                      field
                    )
                  ),
                  id: this.formBuilder.control(sensor.id),
                  name: this.formBuilder.control(sensor.display_name),
                  influx_name: this.formBuilder.control(sensor.influx_name),
                })
              )
          );

          (this.correlationForm.get('sensorFields') as FormArray).push(
            this.formBuilder.group({
              fieldName: field,
              displayName: this.fieldNameForId(field),
              sensors: sensorsArray,
              allSelected: this.formBuilder.control({
                value: false,
                disabled: false,
              }),
            })
          );
        }

        this.generateMinMaxFormGroup();
        this.correlationForm.updateValueAndValidity();
        this.areAllSensorsChecked();

        // Populate available sensors for second y-axis
        this.availableSensorsForSecondYAxis = sensors.sort((a, b) =>
          a.display_name.localeCompare(b.display_name, undefined, {
            numeric: true,
          })
        );

        // Set up second y-axis enabled state and restore configuration
        const enabled = this.data?.secondYAxis?.enabled || false;

        // Update the enabled state in the form
        const secondYAxisGroup = this.correlationForm.get(
          'secondYAxis'
        ) as FormGroup;
        secondYAxisGroup.patchValue({
          enabled: enabled,
        });

        this.updateSecondYAxisValidation(enabled);
        this.restoreSecondYAxisConfiguration();
      });
  }

  private generateMinMaxFormGroup() {
    Array.from(this.availableSensors.keys()).forEach((field) => {
      // Add min max fields to form
      let minMax: MinMaxValue = {
        id: field,
        min: 0,
        max: 0,
      };

      if (this.data?.guid) {
        minMax = getMinMaxFromConfig(field, this.data.min_max);
      }

      this.yAxisMinValuesFormGroup().push(
        new UntypedFormGroup({
          id: new UntypedFormControl(field),
          min: new UntypedFormControl(minMax.min, [Validators.required]),
          max: new UntypedFormControl(minMax.max, [Validators.required]),
        })
      );
    });
  }

  public updateOrCreateModule() {
    if (!this.correlationForm.valid) {
      return;
    }

    this.submitting = true;
    const minMaxValues = this.correlationForm.value.yAxisMinValues.map(
      (group, index) => group
    );

    this.reportModuleService
      .addOrEditModuleToReport(
        <GenericCorrelationConfig>{
          guid: this.correlationForm.value.guid,
          type: this.correlationForm.value.type,
          headline: this.correlationForm.value.headline,
          x_label: this.correlationForm.value.labelXAxis,
          y_label: this.correlationForm.value.labelYAxis,
          filter: {
            enabled: this.correlationForm.value.timePeriodFiltered,
            hourStart: this.correlationForm.value.timePeriodFilteredFrom,
            hourEnd: this.correlationForm.value.timePeriodFilteredTill,
          },
          user_settings: {
            start_date: null,
            end_date: null,
          },
          min_max: minMaxValues,
          relative_zero: this.correlationForm.value.relativeZero,
          sensorFields: this.correlationForm.value.sensorFields,
          defaultSelectedField: this.correlationForm.value.defaultSelectedField,
          defaultDateRange: this.correlationForm.value.defaultDateRange,
          defaultLowPassFilter: this.correlationForm.value.defaultLowPassFilter,
          secondYAxis: this.correlationForm.value.secondYAxis,
          // sensorFields: newSensorFields,
        },
        this.editMode
      )
      .subscribe(
        (success) => {
          this.submitting = false;
          this.dialogRef.close();
          this.dataChangedService.configUpdated(
            this.reportModuleService.currentReport.id,
            this.correlationForm.value.guid
          );
        },
        (error) => {
          this.submitting = false;
          console.error(error);
        }
      );
  }

  get timeFilterDisabled(): string | null {
    return this.correlationForm.get('timePeriodFiltered').value ? null : '';
  }

  private yAxisMinValuesFormGroup(): UntypedFormArray {
    return this.correlationForm.get('yAxisMinValues') as UntypedFormArray;
  }

  private datasourcesFromFormGroup(): UntypedFormArray {
    return this.correlationForm.get('datasources') as UntypedFormArray;
  }

  private visualisableFieldsFromFormGroup(): UntypedFormArray {
    return this.correlationForm.get('visualisableFields') as UntypedFormArray;
  }

  get availableSensorsForForm(): { key: string; value: SensorsSensor[] }[] {
    const res = Array.from(this.availableSensors.entries()).map(
      ([key, value]) => ({
        key,
        value,
      })
    );

    return res;
  }

  onSensorChange(fieldIndex: number, sensorIndex: number): void {
    const fieldGroup = this.fields.at(fieldIndex) as FormGroup;
    const sensorsArray = fieldGroup.get('sensors') as FormArray;

    const allSelected = sensorsArray.controls.every(
      (sensorCtrl) => sensorCtrl.get('checked').value
    );
    fieldGroup.get('allSelected').setValue(allSelected);
    this.updateCheckFieldsFilter();
  }

  public updateCheckFieldsFilter() {
    const selectedFields = this.fields.controls
      .filter((field) => {
        const sensors = (field as FormGroup)
          .get('sensors')
          .value.filter((sensor) => sensor.checked);
        return sensors.length > 0;
      })
      .map((field) => field.value);
    this.selectedFields = selectedFields;

    if (
      !this.data?.defaultSelectedField ||
      this.data?.defaultSelectedField == ''
    ) {
      if (this.selectedFields.length > 0) {
        this.defaultSelectedFieldControl.setValue(
          this.selectedFields[0].fieldName
        );
      }
    } else {
      if (this.selectedFields.length > 0) {
        this.defaultSelectedFieldControl.setValue(
          this.data.defaultSelectedField
        );
      }
    }
  }

  private areAllSensorsChecked(): void {
    for (let i = 0; i < this.fields.length; i++) {
      this.onSensorChange(i, 0);
    }
  }

  get fields(): FormArray {
    return this.correlationForm.get('sensorFields') as FormArray;
  }

  trackByFn(index: number, item: any): any {
    return item.fieldName; // or any unique property of the items
  }

  get sensorFields(): UntypedFormArray {
    return this.correlationForm.get('sensorFields') as UntypedFormArray;
  }

  onAllSelectedChange(fieldIndex: number, isChecked: boolean): void {
    const fieldGroup = this.fields.at(fieldIndex) as FormGroup;
    const sensorsArray = fieldGroup.get('sensors') as FormArray;

    // Set the value of each child sensor's checked property
    for (const sensorCtrl of sensorsArray.controls) {
      (sensorCtrl as FormGroup).get('checked').setValue(isChecked);
    }

    this.updateCheckFieldsFilter();
  }

  public sensorInfoForId(id: string) {
    return this.availableFields.filter((sensor) => sensor.id == id)[0];
  }

  public onSecondYAxisEnabledChange(enabled: boolean): void {
    const secondYAxisGroup = this.correlationForm.get(
      'secondYAxis'
    ) as FormGroup;

    if (!enabled) {
      // Reset form controls when disabled
      secondYAxisGroup.patchValue({
        sensor: null,
        fieldName: '',
        label: '',
        min_max: { min: 0, max: 0 },
      });
      this.availableFieldsForSecondYAxis = [];
    }

    // Update validation based on enabled state
    this.updateSecondYAxisValidation(enabled);
  }

  private updateSecondYAxisValidation(enabled: boolean): void {
    const secondYAxisGroup = this.correlationForm.get(
      'secondYAxis'
    ) as FormGroup;
    const sensorControl = secondYAxisGroup.get('sensor');
    const fieldNameControl = secondYAxisGroup.get('fieldName');
    const labelControl = secondYAxisGroup.get('label');
    const minMaxGroup = secondYAxisGroup.get('min_max') as FormGroup;
    const minControl = minMaxGroup.get('min');
    const maxControl = minMaxGroup.get('max');

    if (enabled) {
      // Add required validators when enabled
      sensorControl.setValidators([Validators.required]);
      fieldNameControl.setValidators([Validators.required]);
      labelControl.setValidators([Validators.required]);
      minControl.setValidators([Validators.required]);
      maxControl.setValidators([Validators.required]);
    } else {
      // Remove validators when disabled
      sensorControl.clearValidators();
      fieldNameControl.clearValidators();
      labelControl.clearValidators();
      minControl.clearValidators();
      maxControl.clearValidators();
    }

    // Update validity
    sensorControl.updateValueAndValidity();
    fieldNameControl.updateValueAndValidity();
    labelControl.updateValueAndValidity();
    minControl.updateValueAndValidity();
    maxControl.updateValueAndValidity();
    secondYAxisGroup.updateValueAndValidity();
    this.correlationForm.updateValueAndValidity();
  }

  public onSecondYAxisSensorChange(sensor: SensorsSensor): void {
    if (!sensor) {
      this.availableFieldsForSecondYAxis = [];
      return;
    }

    // Get available fields for the selected sensor
    this.availableFieldsForSecondYAxis = sensor.available_fields.map(
      (field) => ({
        id: field.identifier,
        label: this.languageService.getTranslatedFieldName(field.field_description),
        unit: field.field_unit,
      })
    );

    // Reset field selection when sensor changes
    const secondYAxisGroup = this.correlationForm.get(
      'secondYAxis'
    ) as FormGroup;
    secondYAxisGroup.patchValue({
      fieldName: '',
      label: '',
      min_max: { min: 0, max: 0 },
    });
  }

  public onSecondYAxisFieldChange(fieldName: string): void {
    if (!fieldName) {return;}

    const field = this.availableFieldsForSecondYAxis.find(
      (f) => f.id === fieldName
    );
    if (field) {
      const secondYAxisGroup = this.correlationForm.get(
        'secondYAxis'
      ) as FormGroup;
      secondYAxisGroup.patchValue({
        label: field.label,
      });
    }
  }

  get secondYAxisFormGroup(): FormGroup {
    return this.correlationForm.get('secondYAxis') as FormGroup;
  }

  private restoreSecondYAxisConfiguration(): void {
    if (!this.data?.secondYAxis?.enabled || !this.data?.secondYAxis?.sensor) {
      return;
    }

    // Check if the saved sensor still exists in current data
    const savedSensor = this.data.secondYAxis.sensor;
    const currentSensor = this.availableSensorsForSecondYAxis.find(
      (sensor) => sensor.id === savedSensor.id
    );

    if (!currentSensor) {
      // Sensor no longer exists, reset the configuration
      const secondYAxisGroup = this.correlationForm.get(
        'secondYAxis'
      ) as FormGroup;
      secondYAxisGroup.patchValue({
        enabled: false,
        sensor: null,
        fieldName: '',
        label: '',
        min_max: { min: 0, max: 0 },
      });
      return;
    }

    // Set the sensor value in the form
    const secondYAxisGroup = this.correlationForm.get(
      'secondYAxis'
    ) as FormGroup;
    secondYAxisGroup.patchValue({
      sensor: currentSensor,
    });

    // Restore the sensor selection and populate fields
    this.onSecondYAxisSensorChange(currentSensor);

    // Wait for the next tick to ensure fields are populated, then restore field selection
    setTimeout(() => {
      if (this.data.secondYAxis.fieldName) {
        // Check if the saved field still exists for this sensor
        const fieldExists = currentSensor.available_fields.some(
          (field) => field.identifier === this.data.secondYAxis.fieldName
        );

        if (fieldExists) {
          secondYAxisGroup.patchValue({
            fieldName: this.data.secondYAxis.fieldName,
            label: this.data.secondYAxis.label || '',
            min_max: {
              min: this.data.secondYAxis.min_max?.min || 0,
              max: this.data.secondYAxis.min_max?.max || 0,
            },
          });
        } else {
          // Field no longer exists, reset field selection
          secondYAxisGroup.patchValue({
            fieldName: '',
            label: '',
            min_max: { min: 0, max: 0 },
          });
        }
      }

      // Update validation after restoring values
      this.updateSecondYAxisValidation(true);
    }, 0);
  }

  /**
   * Copy sensor field selection to clipboard
   */
  public copyFieldSelection(fieldIndex: number): void {
    const success = this.copyPasteService.copyField(
      fieldIndex,
      this.sensorFields
    );

    if (success) {
      const fieldName = this.sensorFields.at(fieldIndex).value.displayName;
      this.snackBar.open(
        `Sensor-Auswahl von "${fieldName}" wurde kopiert`,
        'OK',
        { duration: 3000 }
      );
    } else {
      this.snackBar.open('Fehler beim Kopieren der Sensor-Auswahl', 'OK', {
        duration: 3000,
      });
    }
  }

  /**
   * Paste sensor field selection from clipboard
   */
  public pasteFieldSelection(fieldIndex: number): void {
    const success = this.copyPasteService.pasteField(
      fieldIndex,
      this.sensorFields
    );

    if (success) {
      const fieldName = this.sensorFields.at(fieldIndex).value.displayName;
      const copiedFieldName = this.copyPasteService.getCopiedFieldInfo();

      // Update the allSelected state for the target field
      this.onSensorChange(fieldIndex, 0);

      // Update the selected fields filter
      this.updateCheckFieldsFilter();

      this.snackBar.open(
        `Sensor-Auswahl von "${copiedFieldName}" wurde in "${fieldName}" eingefügt`,
        'OK',
        { duration: 3000 }
      );
    } else {
      this.snackBar.open('Fehler beim Einfügen der Sensor-Auswahl', 'OK', {
        duration: 3000,
      });
    }
  }

  /**
   * Check if there's a copied field available
   */
  public hasCopiedField(): boolean {
    return this.copyPasteService.hasCopiedField();
  }
}
